import React from 'react'
import { CheckinFeature, Feature } from '../api/client'
import ReferenceValueDisplay from './ReferenceValueDisplay'
import { resolveCollectionItemName } from '../utils/collectionUtils'
import { boolLabelFor } from '../utils/boolFeature'
import { useIsMobile } from '../hooks/useIsMobile'

interface ListValueDisplayProps {
  feature: Feature
  /** Active rows for the feature (caller filters out DELETE tombstones / empty
   *  placeholder rows). Order is preserved — the chip surfaces the first
   *  `maxValues` rows and rolls the rest into the tail. */
  rows: CheckinFeature[]
  collectionItems: Record<string, any>
  maxValues?: number
}

// Per-element chip cap. Pairs with `whitespace-nowrap` on the parent chip.
// Mobile is much tighter — phone width can't accommodate desktop-sized chips.
// Mirrors MapValueDisplay's segment caps so list chips and map chips read at
// the same density.
const ELEM_LEN_DESKTOP = 30
const ELEM_LEN_MOBILE = 14
const makeTruncate = (max: number) => (s: string): string =>
  s.length > max ? `${s.slice(0, max - 1)}…` : s

/**
 * Compact, read-only summary of a list-typed feature's rows. Renders
 * `: val1, val2, and N more` (or `: val1, val2, more` on mobile), with
 * per-element truncation and a maximum visible count.
 *
 * The leading `: ` is owned by this component — same contract as
 * `MapValueDisplay`. Callers should NOT re-prefix.
 *
 * Single source of truth for list chip display across the timeline
 * (`CheckinItem`) and the composer (`EnhancedFeatureToken`). Future tweaks
 * (new subtypes, tighter caps, prefix glyphs) should land here so both
 * surfaces stay in sync automatically.
 */
const ListValueDisplay: React.FC<ListValueDisplayProps> = ({
  feature,
  rows,
  collectionItems,
  maxValues = 2,
}) => {
  const isMobile = useIsMobile()
  const elemLen = isMobile ? ELEM_LEN_MOBILE : ELEM_LEN_DESKTOP
  const truncate = makeTruncate(elemLen)
  const listType = feature.config?.params?.type
  const unit = listType === 'num' && feature.unit ? feature.unit : ''

  if (rows.length === 0) return null

  // Resolve each row into a renderable node. Drop rows that don't resolve
  // (e.g. a reference whose target isn't in the collectionItems cache yet,
  // a location row with neither a reference_value nor coords) so the chip
  // doesn't flash a stray `#42` or `?` while the cache hydrates.
  const renderedRows: React.ReactNode[] = []
  for (let i = 0; i < rows.length; i++) {
    const cf = rows[i]
    let node: React.ReactNode | null = null
    if (listType === 'reference') {
      if (cf.reference_value != null) {
        node = (
          <ReferenceValueDisplay
            itemId={cf.reference_value}
            showPrefix={false}
            collectionItems={collectionItems}
            maxLen={elemLen}
          />
        )
      }
    } else if (listType === 'location') {
      if (cf.reference_value != null) {
        const name = resolveCollectionItemName(cf.reference_value, collectionItems)
        if (name) node = truncate(name)
      } else if (cf.latitude != null && cf.longitude != null) {
        node = `${cf.latitude.toFixed(2)}°, ${cf.longitude.toFixed(2)}°`
      }
    } else if (listType === 'num') {
      if (cf.decimal_value != null) node = `${cf.decimal_value}${unit}`
    } else if (listType === 'bool') {
      const label = boolLabelFor(cf.decimal_value, feature.config?.params)
      if (label != null) node = truncate(label)
    } else {
      // 'str' / 'enum' / unknown — both store the value in freeform_value.
      if (cf.freeform_value != null) node = truncate(String(cf.freeform_value))
    }
    if (node != null) renderedRows.push(node)
  }

  if (renderedRows.length === 0) return null

  const displayCount = Math.min(maxValues, renderedRows.length)
  const remaining = renderedRows.length - displayCount

  return (
    <span>
      :{' '}
      {renderedRows.slice(0, displayCount).map((node, idx) => (
        <span key={idx}>
          {node}
          {idx < displayCount - 1 && ', '}
        </span>
      ))}
      {remaining > 0 && (
        <span>{isMobile ? ', more' : `, and ${remaining} more`}</span>
      )}
    </span>
  )
}

export default ListValueDisplay
